import CryptoJS from 'crypto-js';
import { User } from 'oidc-client-ts';

import logger from '../logger';

import asyncStorage from './asyncStorage';

const TIMESTAMP_DELIMITER = '||';
const MAX_TIMESTAMP_SIZE = 24;
const DEFAULT_CLEAR_NO = 5;
// one week
const CLEAR_BEFORE_DAYNO = 7;
const DEFAULT_SALT = process.env.REACT_APP_DEFAULT_SALT!;
const CACHE_PREFIX = 'cache';
const CACHE_OIDC_USER_KEY = 'NL_OIDC_USER';

const getCacheKey = (key: string) => `${CACHE_PREFIX}_${key}`;
const isCacheKey = (key: string) => key.startsWith(CACHE_PREFIX);
const appendTimeStamp = (value: string) => Date.now() + TIMESTAMP_DELIMITER + value;

export const getTimeStamp = (value: string) => {
  const idx = value.substring(0, MAX_TIMESTAMP_SIZE).indexOf(TIMESTAMP_DELIMITER);
  return idx > 0 ? Number(value.substring(0, idx)) : 0;
};
// cut timestamp
const cutTimeStamp = (value: string) => {
  const idx = value.substring(0, MAX_TIMESTAMP_SIZE).indexOf(TIMESTAMP_DELIMITER);
  return idx > 0 ? value.substring(idx + TIMESTAMP_DELIMITER.length) : value;
};

type KeyAndTime = {
  key: string;
  timeStamp: number;
};
type OfflineUsers = Record<string | '$root$', User>;

export const clearOfflineUsers = async () => {
  await asyncStorage.removeItem(CACHE_OIDC_USER_KEY);
};

export const getOfflineUsers = async () => {
  const encrypted = await asyncStorage.getItem(CACHE_OIDC_USER_KEY);
  if (!encrypted) {
    return undefined;
  }

  try {
    const jsonStr = CryptoJS.AES.decrypt(encrypted, DEFAULT_SALT).toString(CryptoJS.enc.Utf8);
    return (JSON.parse(jsonStr) || {}) as OfflineUsers;
  } catch (error) {
    logger.error(`Decryption failed to users!`, error);
    return undefined;
  }
};

const getOfflineUserKey = (moduleId: string | null) => moduleId ?? '$root$';

export const getOfflineUser = async (moduleId: string | null) => {
  const offlineUsers = await getOfflineUsers();
  if (!offlineUsers) {
    return undefined;
  }

  return offlineUsers[getOfflineUserKey(moduleId)];
};

export const setOfflineUser = async (user: User, moduleId: string | null) => {
  const offlineUsers = (await getOfflineUsers()) || {};
  offlineUsers[getOfflineUserKey(moduleId)] = user;

  const encrypted = CryptoJS.AES.encrypt(JSON.stringify(offlineUsers), DEFAULT_SALT).toString();
  const result = await asyncStorage.setItem(CACHE_OIDC_USER_KEY, encrypted);
  if (!result) {
    throw new Error('encryptedTimestampCache set user failed');
  }
  return result;
};

const encryptedTimestampCache = (() => {
  let password: string = DEFAULT_SALT;
  const setPassword = (pwd: string) => {
    password = pwd;
  };
  const getPassword = () => password;

  const pushInOrder = (keysAndTimes: KeyAndTime[], key: string, newTimeStamp: number) => {
    const idx = keysAndTimes.findIndex(({ timeStamp }) => newTimeStamp < timeStamp);
    if (idx < 0) {
      keysAndTimes.push({ key, timeStamp: newTimeStamp });
    } else {
      keysAndTimes.splice(idx, 0, { key, timeStamp: newTimeStamp });
    }
  };

  /**
   * Clear cached values, that stored more days before, than the given day parameter
   * @param clearBeforeDayNo The day number to delete cache before
   */
  const clearCacheBefore = async (clearBeforeDayNo: number = CLEAR_BEFORE_DAYNO) => {
    const timeDiff = clearBeforeDayNo * 24 * 60 * 60 * 1000;
    const keysToDelete: string[] = [];
    const now = Date.now();
    const success = await asyncStorage.iterate((value, key) => {
      const time = typeof value === 'string' ? getTimeStamp(value) : 0;
      if (time > 0 && now - time > timeDiff) {
        keysToDelete.push(key);
      }
    });
    if (!success) {
      return;
    }
    await Promise.all(keysToDelete.map((key) => asyncStorage.removeItem(key)));
    logger.info(`Deleted ${keysToDelete.length} item from encrypted cache`);
  };

  const clearCache = async () => {
    await asyncStorage.iterate((value, key) => {
      if (key.startsWith(CACHE_PREFIX)) {
        asyncStorage.removeItem(key);
      }
    });
  };

  const clearLastCaches = async (clearNo: number = DEFAULT_CLEAR_NO) => {
    const keysAndTimes: KeyAndTime[] = [];
    const collectMinValues = (key: string, timeStamp: number) => {
      if (isCacheKey(key) && timeStamp > 0) {
        if (keysAndTimes.length < clearNo) {
          pushInOrder(keysAndTimes, key, timeStamp);
        } else if (timeStamp < keysAndTimes[clearNo - 1].timeStamp) {
          keysAndTimes.pop();
          pushInOrder(keysAndTimes, key, timeStamp);
        }
      }
    };
    const success = await asyncStorage.iterate((value, key) => {
      collectMinValues(key, getTimeStamp(value));
    });
    if (!success) {
      return;
    }
    await Promise.all(keysAndTimes.map((keyAndTime) => asyncStorage.removeItem(keyAndTime.key)));
    logger.info(`Last ${keysAndTimes.length} encrypted cache item deleted`);
  };

  const getItem = async (key: string) => {
    const encrypted = await asyncStorage.getItem(getCacheKey(key));
    if (!encrypted || typeof encrypted !== 'string') {
      logger.warn(`Cached response not found to key: ${key}!`);
      return undefined;
    }
    try {
      const val = CryptoJS.AES.decrypt(cutTimeStamp(encrypted), password).toString(CryptoJS.enc.Utf8);
      if (val === '') {
        logger.error(`Decryption failed to key ${key} result is empty!`);
        return undefined;
      }
      return val;
    } catch (error) {
      logger.error(`Decryption failed to key ${key}!`, error);
      return undefined;
    }
  };

  const setItem = async (key: string, value: string) => {
    const encrypted = CryptoJS.AES.encrypt(value, password).toString();
    const result = await asyncStorage.setItem(getCacheKey(key), appendTimeStamp(encrypted));
    if (!result) {
      logger.warn('encryptedTimestampCache set item failed, try to clear last caches');
      await clearLastCaches();
    }
    return result;
  };

  const removeItem = async (key: string) => {
    const result = await asyncStorage.removeItem(getCacheKey(key));
    return result;
  };

  return {
    clearCache,
    clearLastCaches,
    clearOfflineUsers,
    clearCacheBefore,
    getItem,
    setItem,
    removeItem,
    getPassword,
    setPassword,
  };
})();

export default encryptedTimestampCache;
